import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import {
  IActiveTripResponse,
  IAssistanceCategoryObject,
  IAssistanceObject,
  IAutoComplete,
  Id,
  IMembership,
  IMembershipObject,
  IMobilityOptionObject,
  IOrganizationTeamMember,
  IOrgObj,
  IProgramServiceObject,
  IProviderObject,
  IProviderProductObject,
  IRiderObject,
  IRiderLocationObject,
  IStateOrganizationTeamMember,
  IStateUser,
  IStoreState,
  ITripHistoryObject,
  IUserTrips,
  StateOrganizationTeamMemberMap,
  StateUserMap,
  StateUserRoleMap,
  IPromotion
} from '../../types'
import { getCurrentOrganizationId } from '../reducers/organizations'
import { TripStages } from '../../constants/trip-statuses'

export const currentOrgId = (state: IStoreState): number => state.currentOrg.id!
export const programServices = (state: IStoreState): { [key: number]: IProgramServiceObject } =>
  state.programService.items
export const providers = (state: IStoreState): { [key: number]: IProviderObject } => state.provider.items
export const providerProducts = (state: IStoreState): { [key: number]: IProviderProductObject } =>
  state.providerProduct.items
export const users = (state: IStoreState): StateUserMap => state.user.items
export const roles = (state: IStoreState): StateUserRoleMap => state.teamMember.roles
export const userTrips = (state: IStoreState): IUserTrips[] => state.tripRequest.settings.userTrips
export const pickupAutoComplete = (state: IStoreState): IAutoComplete[] => state.tripRequest.settings.pickupAutoComplete
export const dropoffAutoComplete = (state: IStoreState): IAutoComplete[] =>
  state.tripRequest.settings.dropoffAutoComplete
export const additionalStopAutoComplete = (state: IStoreState): IAutoComplete[] =>
  state.tripRequest.settings.additionalStopAutoComplete

export const organizations = (state: IStoreState): { [key: number]: IOrgObj } => state.organizations.items

export const membership = (state: IStoreState): { [key: string]: IMembershipObject } => state.membership.items
export const selectedMembership = (state: IStoreState): IMembership | null => state.membership.selectedMembership
export const updatingStatus = (state: IStoreState): string => state.membership.updatingStatus

export const activeTrips = (state: IStoreState): IActiveTripResponse[] => state.tripRequest.settings.activeTrips
export const tripsHistory = (state: IStoreState): ITripHistoryObject[] => state.tripsHistory.items
export const tripHistory = (state: IStoreState): any => state.tripHistory
export const tripsHistoryColumns = (state: IStoreState): string[] => state.tripsHistory.activeColumns

export const unCachedProducts = (state: IStoreState): IProviderProductObject[] => {
  const products = Object.values(state.providerProduct.items)
  return products
}

export const product = (state: IStoreState, productId: string) => {
  return state.providerProduct.items[productId]
}

export const getRecentTrips = createCachedSelector(
  userTrips,
  (state: IStoreState, limiter: number) => limiter,
  (items, limiter) => items.filter((item: any) => item.status === 'RECENT').slice(0, limiter)
)((state: IStoreState) => state.tripRequest.settings.riderId)

export const getFavoriteTrips = createCachedSelector(
  userTrips,
  (state: IStoreState, limiter: number) => limiter,
  (items, limiter) => items.slice(0, limiter)
)((state: IStoreState) => state.tripRequest.settings.riderId)

export const getScheduledTrips = createCachedSelector(
  userTrips,
  (state: IStoreState, limiter: number) => limiter,
  (items, limiter) => items.filter((item: any) => item.status === 'SCHEDULED').slice(0, limiter)
)((state: IStoreState) => state.tripRequest.settings.riderId)

/* pickUpAutoComplete(state)
 * dropOffAutoComplete(state)
 *
 */

export const getPickupAutoComplete = createCachedSelector(pickupAutoComplete, items => items)(
  (state: IStoreState) => state.tripRequest.settings.riderId
)

export const getDropoffAutoComplete = createCachedSelector(dropoffAutoComplete, items => items)(
  (state: IStoreState) => state.tripRequest.settings.riderId
)

export const getAdditionalStopAutoComplete = createCachedSelector(additionalStopAutoComplete, items => items)(
  (state: IStoreState) => state.tripRequest.settings.riderId
)
/* organization's teamMembers
 *
 */
interface IBuildOrganizationTeamMember {
  teamMember: IStateOrganizationTeamMember
  roles?: StateUserRoleMap
  user?: IStateUser
}

export const buildOrganizationTeamMember = ({ teamMember, user, roles }: IBuildOrganizationTeamMember) => {
  let phone = null
  if (user && user.phones.length === 1) {
    phone = user.phones[0]
  }
  if (user && user.phones.length > 1) {
    phone = user.phones.find(it => it.isPrimary)
  }
  const finalRoles = []
  if (user && roles) {
    for (const i of Object.keys(teamMember.roles)) {
      for (const x of Object.keys(roles)) {
        const role: any = roles[x]
        if (teamMember.roles[i] !== role.id) {
          continue
        }
        finalRoles.push(role.roleName)
      }
    }
  }
  return {
    username: user ? user.username : '',
    firstName: user ? user.firstName : '',
    lastName: user ? user.lastName : '',
    countryCode: '',
    id: teamMember.id,
    isArchived: teamMember.isArchived,
    userId: user ? user.id : '',
    name: user && [user.firstName, user.lastName].join(' '),
    roles: finalRoles,
    phoneNumber: (phone && phone.phoneNumber) || 'None',
    email: (user && user.email && user.email.emailAddress) || 'None',
    status: teamMember.status,
    organizationId: teamMember.organization,
    archivedDate: teamMember.archiveDate
  }
}

export const getRequestingTrips = createSelector(
  activeTrips,
  (state: IStoreState, limiter: number) => limiter,
  (items: any, limiter: number) =>
    limiter === 0
      ? items.filter((item: any) => item.statuses[item.statuses.length - 1].name === TripStages.requesting)
      : items
          .filter((item: any) => item.statuses[item.statuses.length - 1].name === TripStages.requesting)
          .slice(0, limiter)
)

export const getWaitingForPickupTrips = createSelector(
  activeTrips,
  (state: IStoreState, limiter: number) => limiter,
  (items: IActiveTripResponse[], limiter: number) =>
    limiter === 0
      ? items.filter((item: any) => item.statuses[item.statuses.length - 1].name === TripStages.waitingForPickUp)
      : items
          .filter((item: any) => item.statuses[item.statuses.length - 1].name === TripStages.waitingForPickUp)
          .slice(0, limiter)
)

export const getEnRouteTrips = createSelector(
  activeTrips,
  (state: IStoreState, limiter: number) => limiter,
  (items: IActiveTripResponse[], limiter: number) =>
    limiter === 0
      ? items.filter((item: any) => item.statuses[item.statuses.length - 1].name === 'EN_ROUTE')
      : items.filter((item: any) => item.statuses[item.statuses.length - 1].name === 'EN_ROUTE').slice(0, limiter)
)

export const getActiveScheduledTrips = createSelector(
  activeTrips,
  (state: IStoreState, limiter: number) => limiter,
  (items: IActiveTripResponse[], limiter: number) =>
    limiter === 0
      ? items.filter((item: any) => item.statuses[item.statuses.length - 1].name === 'SCHEDULED_TRIPS')
      : items
          .filter((item: any) => item.statuses[item.statuses.length - 1].name === 'SCHEDULED_TRIPS')
          .slice(0, limiter)
)

export const getFiletedTrips = createSelector(
  activeTrips,
  (state: IStoreState, type: string) => type,
  (items: IActiveTripResponse[], type: string) =>
    items.filter((item: any) => item.statuses[item.statuses.length - 1].name === type)
)

export const getTripsHistory = (state: IStoreState) => {
  const trips = state.tripsHistory.items
  const allTrips = {}
  const buildTrip = (riderTrip: any, trip: any) => {
    const getTime = (date?: Date) => {
      return date ? date.getTime() : 0
    }
    const sortStatus = (status: any[]) => {
      const sorted = status.sort((a: any, b: any) => {
        return getTime(new Date(b.createdAt)) - getTime(new Date(a.createdAt))
      })
      return sorted
    }
    const getStatus = (status: string, statuses: any[]) => {
      for (const i of Object.keys(statuses)) {
        if (statuses[i].status.toLowerCase() === status.toLowerCase()) {
          return statuses[i]
        }
      }
      return null
    }
    //TODO: Check this later. AuditLog might come from the server already and not 'built' from statuses
    const buildAuditLog = (status: any[]) => {
      const lst: any[] = []
      for (const i of Object.keys(status)) {
        const st = status[i]
        lst.push({
          date: new Date(st.createdAt),
          log: st.status
        })
      }
      return lst.sort((a: any, b: any) => {
        return getTime(new Date(a.date)) - getTime(new Date(b.date))
      })
    }
    const lastStatus = sortStatus(trip.status)
    const isCompletedStatus = getStatus('COMPLETED', trip.status)
    const isStartedStatus = getStatus('STARTED', trip.status)
    const isAcceptedStatus = getStatus('ACCEPTED', trip.status)
    const isDrivingToPickUpStatus = getStatus('DRIVING_TO_PICK_UP', trip.status)
    const isArrivedAtPickUpStatus = getStatus('ARRIVED_AT_PICK_UP', trip.status)
    const isPickedupStatus = getStatus('PICKED_UP', trip.status)
    const isDrivingToDropOffStatus = getStatus('DRIVING_TO_DROP_OFF', trip.status)
    const isArrivedAtDropOffStatus = getStatus('ARRIVED_AT_DROP_OFF', trip.status)

    // Time between accepted and started
    const wasRequestingTime =
      isStartedStatus && isAcceptedStatus
        ? (isAcceptedStatus.createdAt.getTime() - isStartedStatus.createdAt.getTime()) / 1000
        : 0
    // pickedup and accepted
    const wasWaitingTime =
      isPickedupStatus && isAcceptedStatus
        ? (isPickedupStatus.createdAt.getTime() - isAcceptedStatus.createdAt.getTime()) / 1000
        : 0
    // pickedup and completed
    const travelTime =
      isPickedupStatus && isCompletedStatus
        ? (isPickedupStatus.createdAt.getTime() - isCompletedStatus.createdAt.getTime()) / 1000
        : 0
    // driving to pick up and arrive
    const pickupDwell =
      isDrivingToPickUpStatus && isArrivedAtPickUpStatus
        ? (isArrivedAtPickUpStatus.createdAt.getTime() - isDrivingToPickUpStatus.createdAt.getTime()) / 1000
        : 0
    // driving to drop off and arrive
    const dropOffDwell =
      isArrivedAtDropOffStatus && isArrivedAtDropOffStatus
        ? (isArrivedAtDropOffStatus.createdAt.getTime() - isArrivedAtDropOffStatus.createdAt.getTime()) / 1000
        : 0

    return {
      id: riderTrip.id,
      rider: Object.assign({}, riderTrip.rider),
      driver: null,
      vehicle: null,
      location: null,
      riderCount: riderTrip.riderCount,
      status: lastStatus[0],
      dropOffTime: isCompletedStatus ? isCompletedStatus.createdAt : null,
      pickUpTime: isStartedStatus ? isStartedStatus.createdAt : null,
      createdAt: new Date(lastStatus[lastStatus.length - 1].createdAt),
      cost: trip.totalCost,
      fare: riderTrip.riderCost,
      service: Object.assign({}, trip.product),
      provider: null,
      feedback: '',
      auditLogs: buildAuditLog(trip.status),
      // TODO: Some of the following must be precalculated in the backend. We're doing it here meanwhile
      wasRequestingTime,
      wasWaitingTime,
      travelTime,
      travelDistance: 0, // TODO: Left this to come from the server
      pickupDwell,
      dropOffDwell,
      wheelchair: 1,
      attendant: true,
      leavingTime: isPickedupStatus ? isPickedupStatus.createdAt : null,
      assistance: 'Deaf'
    }
  }
  for (const i of Object.keys(trips)) {
    const trip = trips[i]
    for (const j of Object.keys(trip.riderTrip)) {
      const riderTrip = trip.riderTrip[j]
      const built = buildTrip(riderTrip, trip)
      allTrips[built.id] = built
    }
  }
  return allTrips
}

export const getTripHistory = createSelector(
  tripHistory,
  tripHistory => tripHistory
)

export const getTripsHistoryColumns = createSelector(
  tripsHistoryColumns,
  columns => columns
)

/* MobilityOptions
 *
 */

export const filterMobilityOptions = (state: IStoreState, query: string): { [key: string]: IMobilityOptionObject } => {
  const filtered: { [key: string]: IMobilityOptionObject } = {}
  const str = new RegExp(query, 'i')
  Object.keys(state.mobilityOptions.items).map((item, i) => {
    const cat = state.mobilityOptions.items[item]
    if (cat.name.match(str)) {
      filtered[cat.id] = cat
    }
  })

  return filtered
}

/* Assistances
 *
 */

export const filterAssistances = (state: IStoreState, query: string): { [key: string]: IAssistanceObject } => {
  const getCategory = (categories: { [key: string]: IAssistanceCategoryObject }, id: string): any => {
    for (const i in categories) {
      if (categories[i].id === id) {
        return categories[i].name
      }
    }
    return null
  }

  const filtered: { [key: string]: IAssistanceObject } = {}
  const str = new RegExp(query, 'i')
  Object.keys(state.assistances.items).map((item, i) => {
    const assistance = state.assistances.items[item]
    const categoryName = getCategory(state.assistanceCategories.items, assistance.categoryId)
    if (assistance.type.match(str) || categoryName.match(str)) {
      filtered[assistance.id] = assistance
    }
  })

  return filtered
}

/* Riders
 *
 */

export const filterRiders = (state: IStoreState, query: string, type: string): { [key: number]: IRiderObject } => {
  const filtered: { [key: number]: IRiderObject } = {}
  const str = new RegExp(query, 'i')
  Object.keys(state.riders.items).map((item, i) => {
    const rider = state.riders.items[item]
    if (
      (rider.firstName && rider.firstName.match(str)) ||
      (rider.lastName && rider.lastName.match(str)) ||
      (rider.email && rider.email.match(str)) ||
      (rider.phone && rider.phone.toString().match(str))
    ) {
      filtered[rider.id] = rider
    }
  })

  return filtered
}

/* Rider Locations
 *
 */

export const filterRiderLocations = (state: IStoreState, query: string): { [key: number]: IRiderLocationObject } => {
  const filtered: IRiderLocationObject[] = []
  const str = new RegExp(query, 'i')
  Object.keys(state.riderLocations.items).map((item, i) => {
    const riderLocation = state.riderLocations.items[item]
    const addressWithName = riderLocation.fullAddress + ' ' + riderLocation.placeName
    if (riderLocation.fullAddress && str.test(addressWithName)) {
      filtered.push(riderLocation)
    }
  })
  return filtered
}

/* Membership
 *
 */
export const getMemberships = createSelector(
  membership,
  getCurrentOrganizationId,
  (items: { [key: string]: IMembershipObject }, currentOrgId?: string) => {
    return Object.values(items).filter(it => it)
  }
)

export const getSelectedMembership = createSelector(
  selectedMembership,
  selected => selected
)

export const getUpdatingStatus = createSelector(
  updatingStatus,
  status => status
)

/* Organizations
 *
 */
export const getOrganizations = createSelector(
  organizations,
  items => items
)

export const getOrganization = (id: Id, state: IStoreState) => organizations(state)[id]
export const getVehicleType = (id: Id, state: IStoreState) => {
  for (const i of Object.keys(state.vehicleCreateOptions.carTypes)) {
    if (state.vehicleCreateOptions.carTypes[i].id === id) {
      return {
        label: state.vehicleCreateOptions.carTypes[i].name,
        id: state.vehicleCreateOptions.carTypes[i].id
      }
    }
  }
  return null
}

export const getVehicleColor = (id: Id, state: IStoreState) => {
  for (const i of Object.keys(state.vehicleCreateOptions.colors)) {
    if (state.vehicleCreateOptions.colors[i].id === id) {
      return {
        label: state.vehicleCreateOptions.colors[i].name,
        id: state.vehicleCreateOptions.colors[i].id
      }
    }
  }
  return null
}

export const getVehicleAccomodation = (id: Id, state: IStoreState) => {
  for (const i of Object.keys(state.vehicleCreateOptions.accommodations)) {
    if (state.vehicleCreateOptions.accommodations[i].id === id) {
      return {
        label: state.vehicleCreateOptions.accommodations[i].name,
        id: state.vehicleCreateOptions.accommodations[i].id
      }
    }
  }
  return null
}

export const getVehicleModel = (id: Id, state: IStoreState) => {
  for (const x of Object.keys(state.vehicleCreateOptions.makes)) {
    const make = state.vehicleCreateOptions.makes[x]
    for (const i of Object.keys(make.models)) {
      if (make.models[i].id === id) {
        return {
          label: make.models[i].name,
          id: make.models[i].id
        }
      }
    }
  }
  return null
}

export const getVehicleMake = (id: Id, state: IStoreState) => {
  for (const i of Object.keys(state.vehicleCreateOptions.makes)) {
    if (state.vehicleCreateOptions.makes[i].id === id) {
      return {
        label: state.vehicleCreateOptions.makes[i].name,
        id: state.vehicleCreateOptions.makes[i].id
      }
    }
  }
  return null
}

export const getOrgPromotions = (state: IStoreState, curOrgId: string, isActive?: boolean): IPromotion[] => {
  const activeFilter = isActive === false ? false : true
  return Object.values(state.organizationPromotions.items).filter(
    ({ orgId, active }) => orgId === curOrgId && active === activeFilter
  )
}



// WEBPACK FOOTER //
// ./src/state/selectors/index.ts